/**
 * Banner de proveniência no editor de apreciação.
 *
 * Como a apreciação gerada por IA é gravada direto (sem tela de revisão), este
 * banner é a rede de segurança: mostra de onde veio cada campo, quais ficaram
 * com baixa confiança e quais valores de HRN o sistema precisou rebaixar por
 * estarem fora da distribuição real do acervo.
 */

import { useEffect, useMemo, useState } from 'react';
import { Sparkles, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { apiService } from '@/services/api';
import { useI18n } from '@/contexts/i18n-hook';
import type { IaJob, ProvenienciaCampo } from '@/types/apreciacao-ia';

const COR_ORIGEM: Record<ProvenienciaCampo['origem'], string> = {
  constante: 'bg-gray-100 text-gray-700',
  banco: 'bg-blue-100 text-blue-800',
  planilha: 'bg-green-100 text-green-800',
  ia: 'bg-purple-100 text-purple-800',
  usuario: 'bg-amber-100 text-amber-800',
  derivado: 'bg-sky-100 text-sky-800',
};

/**
 * Nomes técnicos que têm rótulo de seção no dicionário; o nome do campo é o
 * próprio caminho da chave (`apreciacaoIa.banner.campo.maquina.nome`).
 */
const CAMPOS_COM_ROTULO = new Set([
  'titulo',
  'emitente',
  'demanda',
  'descricao',
  'nomeMaquina',
  'tipoMaquina',
  'identificacaoMaquina',
  'setorMaquina',
  'tensaoAcionamento',
  'tensaoComando',
  'operadorEntrevistado',
  'cargoOperador',
  'dataEntrevista',
  'limitesUso',
  'limitesEspaco',
  'limitesAdicionais',
  'historicoAcidentes',
  'listaVerificacaoAdministrativa',
  'executanteExterno',
  'elaboradorExterno',
  'verificadorExterno',
  'responsavelTecnicoExterno',
  'maquina.nome',
  'maquina.tipo',
  'maquina.local',
  'maquina.area',
  'maquina.site',
  'maquina.descricao',
  'maquina.fabricante',
  'maquina.modelo',
  'maquina.serie',
  'maquina.ano',
]);

interface Props {
  apreciacaoId: string;
  /** Pasta de imagens vinculada, usada para localizar o job. */
  machineFolder?: string | null;
  projetoId?: string | null;
}

export function BannerProvenienciaIA({ apreciacaoId, machineFolder, projetoId }: Props) {
  const { t, formatarData } = useI18n();
  const [job, setJob] = useState<IaJob | null>(null);
  const [expandido, setExpandido] = useState(false);

  useEffect(() => {
    if (!projetoId || !machineFolder) return;

    let cancelado = false;
    apiService
      .statusIaPorPasta(projetoId, [machineFolder])
      .then(async (r) => {
        if (cancelado || !r.success) return;
        const item = r.data?.itens?.[0];
        // Só mostra o banner se o job realmente gerou ESTA apreciação.
        if (!item?.jobId || item.apreciacaoId !== apreciacaoId) return;

        const j = await apiService.obterJobIA(item.jobId);
        if (!cancelado && j.success && j.data) setJob(j.data);
      })
      .catch(() => {
        // Ausência do banner não deve afetar o editor.
      });

    return () => {
      cancelado = true;
    };
  }, [apreciacaoId, machineFolder, projetoId]);

  const campos = useMemo(() => Object.entries(job?.proveniencia ?? {}), [job]);

  const baixaConfianca = useMemo(
    () => campos.filter(([, p]) => p.confianca === 'baixa' || p.confianca === 'ausente'),
    [campos]
  );

  const ajustesHrn = useMemo(
    () => (job?.avisos ?? []).filter((a) => a.mensagem.startsWith('HRN de ')),
    [job]
  );

  const outrosAvisos = useMemo(
    () => (job?.avisos ?? []).filter((a) => !a.mensagem.startsWith('HRN de ')),
    [job]
  );

  if (!job || job.status !== 'concluido') return null;

  const rotuloCampo = (campo: string) =>
    CAMPOS_COM_ROTULO.has(campo) ? t(`apreciacaoIa.banner.campo.${campo}`) : campo;

  const dataGeracao = job.finalizadoEm ? formatarData(job.finalizadoEm) : '';

  return (
    <div className="mb-4 rounded-lg border border-purple-200 bg-purple-50">
      <button
        type="button"
        onClick={() => setExpandido((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left"
      >
        {expandido ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-purple-600" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-purple-600" />
        )}
        <Sparkles className="h-4 w-4 shrink-0 text-purple-600" />
        <span className="flex-1 text-sm text-purple-900">
          <strong>{t('apreciacaoIa.banner.titulo')}</strong>
          {dataGeracao && t('apreciacaoIa.banner.em', { data: dataGeracao })}
          {' · '}
          {baixaConfianca.length > 0 && (
            <span className="font-medium text-amber-800">
              {t('apreciacaoIa.banner.baixaConfianca', { total: baixaConfianca.length })} ·{' '}
            </span>
          )}
          {ajustesHrn.length > 0 && (
            <>
              {t('apreciacaoIa.banner.ajustesHrn', { total: ajustesHrn.length })}
              {' · '}
            </>
          )}
          {t('apreciacaoIa.banner.revise')}
        </span>
      </button>

      {expandido && (
        <div className="space-y-4 border-t border-purple-200 px-4 py-3">
          {baixaConfianca.length > 0 && (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2">
              <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-amber-900">
                <AlertTriangle className="h-3.5 w-3.5" />
                {t('apreciacaoIa.banner.confiraCampos')}
              </div>
              <ul className="space-y-0.5 text-xs text-amber-900">
                {baixaConfianca.map(([campo, p]) => (
                  <li key={campo}>
                    <strong>{rotuloCampo(campo)}</strong>
                    {p.detalhe ? ` — ${p.detalhe}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {outrosAvisos.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-purple-800">
                {t('apreciacaoIa.banner.avisosTitulo')}
              </p>
              <ul className="list-inside list-disc space-y-0.5 text-xs text-gray-700">
                {outrosAvisos.map((a, i) => (
                  <li key={i}>{a.mensagem}</li>
                ))}
              </ul>
            </div>
          )}

          {ajustesHrn.length > 0 && (
            <details>
              <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-purple-800">
                {t('apreciacaoIa.banner.ajustesTitulo', { total: ajustesHrn.length })}
              </summary>
              <ul className="mt-1 list-inside list-disc space-y-0.5 text-xs text-gray-700">
                {ajustesHrn.map((a, i) => (
                  <li key={i}>{a.mensagem}</li>
                ))}
              </ul>
            </details>
          )}

          {campos.length > 0 && (
            <details>
              <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-purple-800">
                {t('apreciacaoIa.banner.origemTitulo', { total: campos.length })}
              </summary>
              <div className="mt-2 grid gap-1 sm:grid-cols-2">
                {campos.map(([campo, p]) => (
                  <div key={campo} className="flex items-center gap-2 text-xs">
                    <span
                      className={`shrink-0 rounded px-1.5 py-0.5 font-medium ${COR_ORIGEM[p.origem]}`}
                    >
                      {t(`apreciacaoIa.banner.origem.${p.origem}`)}
                    </span>
                    <span className="truncate text-gray-700" title={p.detalhe}>
                      {rotuloCampo(campo)}
                    </span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
